import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Copy, Languages, Loader2, AlertTriangle, Info } from "lucide-react";

export function HreflangGuidePanel({ issue, siteId, onClose }) {
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.post("/seo-fix/hreflang-guide", { site_id: siteId, page_url: issue.page_url })
      .then(({ data }) => setGuide(data))
      .catch((e) => setError(e?.response?.data?.detail || "Impossible de générer le guide hreflang"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copié`);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="hreflang-guide-panel">
      <div className="absolute inset-0 bg-slate-950/30" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl p-6">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-display text-lg font-bold text-slate-950 flex items-center gap-2">
            <Languages className="w-4 h-4 text-[#002FA7]" /> Guide hreflang
          </h2>
          <button onClick={onClose} data-testid="hreflang-guide-close" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <a href={issue.page_url} target="_blank" rel="noreferrer" className="text-xs text-[#002FA7] hover:underline break-all">{issue.page_url}</a>
        <div className="mt-2 text-sm text-slate-900 font-medium">{issue.message}</div>
        <div className="text-xs text-slate-600">{issue.recommendation}</div>

        {loading ? (
          <div className="mt-5 border border-slate-200 rounded-md px-3 py-4 text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Lecture de la page et construction du guide…
          </div>
        ) : error ? (
          <div className="mt-5 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="hreflang-guide-error">{error}</div>
        ) : guide && (
          <>
            <div className="mt-4 flex items-center gap-2">
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Langue de la page</span>
              <span data-testid="hreflang-page-lang" className="text-[10px] font-bold px-1.5 py-0.5 rounded border border-slate-300 bg-slate-100 text-slate-700 uppercase">{guide.page_lang}</span>
              {!guide.had_hreflang && (
                <span className="text-xs text-slate-500">— aucune déclaration hreflang trouvée sur cette page</span>
              )}
            </div>

            {guide.warnings?.length > 0 && (
              <div className="mt-4 border border-amber-200 bg-amber-50 rounded-md p-3" data-testid="hreflang-guide-warnings">
                <ul className="space-y-1.5">
                  {guide.warnings.map((w, i) => (
                    <li key={i} className="text-xs text-amber-900 flex items-start gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" /> {w}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-5">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Jeu hreflang corrigé — dans le &lt;head&gt; de chaque version</div>
                <button onClick={() => copy(guide.html_snippet, "Bloc HTML")} data-testid="hreflang-copy-html"
                  className="inline-flex items-center gap-1 text-xs text-slate-600 hover:underline">
                  <Copy className="w-3 h-3" /> Copier
                </button>
              </div>
              <pre data-testid="hreflang-html-snippet" className="border border-slate-200 bg-slate-50 rounded-md px-3 py-2 text-xs text-slate-800 overflow-x-auto whitespace-pre-wrap break-all">{guide.html_snippet}</pre>
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Sitemap.xml — dans l&apos;entrée &lt;url&gt; de chaque version</div>
                <button onClick={() => copy(guide.sitemap_snippet, "Bloc sitemap")} data-testid="hreflang-copy-sitemap"
                  className="inline-flex items-center gap-1 text-xs text-slate-600 hover:underline">
                  <Copy className="w-3 h-3" /> Copier
                </button>
              </div>
              <pre data-testid="hreflang-sitemap-snippet" className="border border-slate-200 bg-slate-50 rounded-md px-3 py-2 text-xs text-slate-800 overflow-x-auto whitespace-pre-wrap break-all">{guide.sitemap_snippet}</pre>
            </div>

            <div className="mt-5">
              <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Étapes pour votre plateforme</div>
              <ol className="space-y-2" data-testid="hreflang-platform-steps">
                {(guide.platform_steps || []).map((s, i) => (
                  <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-[#002FA7] text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                    {s}
                  </li>
                ))}
              </ol>
            </div>

            <div className="mt-5 text-xs text-slate-600 bg-blue-50/60 border border-blue-100 rounded-md px-3 py-2 flex items-start gap-2" data-testid="hreflang-guide-note">
              <Info className="w-3.5 h-3.5 text-[#002FA7] mt-0.5 flex-shrink-0" />
              Correction manuelle uniquement : aucun connecteur (Pont Emergent, Velo, GitHub auto) n&apos;écrit les hreflang — Google lit le HTML source et le sitemap. Rien n&apos;est modifié par ce guide.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
